import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  UsersIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  TrophyIcon,
  GraduationCapIcon,
  SearchIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../data/learningLevels.json';
import { getDepartmentTranslation } from '../../services/departmentsTranslationsService';

/** نفس الحقول الافتراضية لصفحة إدارة الطلبة (Students.js) */
const DEFAULT_STUDENT_FILTERS = {
  isActive: '',
  sortBy: 'first_name',
  sortOrder: 'asc',
  learningLevelId: '',
  departmentId: ''
};

const CompetitionStudents = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { competitionId } = useParams();

  // State
  const [competition, setCompetition] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  
  // Selected students state
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [isAdding, setIsAdding] = useState(false);
  
  // Existing participants (already added to competition) — مفاتيح user id كنص موحّد
  const [existingParticipants, setExistingParticipants] = useState(new Set());

  const [filters, setFilters] = useState(() => ({ ...DEFAULT_STUDENT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState({});
  const [learningLevels, setLearningLevels] = useState([]);
  const [filterDepartments, setFilterDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Helper function to translate level name
  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  // Fetch competition details
  const fetchCompetitionDetails = async () => {
    try {
      const response = await api.get('/competitions?page=1&limit=100');
      
      if (response.data.success) {
        const allCompetitions = response.data.data || [];
        const currentCompetition = allCompetitions.find(comp => comp.competitionId === competitionId);
        
        if (currentCompetition) {
          setCompetition(currentCompetition);
        } else {
          toast.error(t('competitions.notFound'), { duration: 3000, position: 'top-center' });
        }
      }
    } catch (error) {
      console.error('Error fetching competition details:', error);
      toast.error(t('competitions.fetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch existing participants
  const fetchExistingParticipants = async () => {
    try {
      const response = await api.get(`/competitions/${competitionId}/participants?page=1&limit=500`);
      
      if (response.data.success) {
        const participants = response.data.data?.participants || response.data.data || [];

        const participantUserIds = new Set();
        participants.forEach((p) => {
          const uid = p.studentUserId ?? p.user_id ?? p.userId;
          if (uid == null) return;
          participantUserIds.add(String(uid));
        });
        setExistingParticipants(participantUserIds);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      // إذا كانت المسابقة لا تحتوي على مشاركين بعد أو حدث خطأ، نعتبر أنه لا يوجد مشاركين
      setExistingParticipants(new Set());
    }
  };

  /** نفس منطق صفحة إدارة الطلبة (Students.js): /students أو /students/level/:id أو organization/filter */
  const fetchStudents = async (page = 1, search = '', filterParams = {}, itemsPerPage = limit) => {
    setLoading(true);
    try {
      const baseParams = new URLSearchParams();
      baseParams.set('page', page);
      baseParams.set('limit', itemsPerPage);
      if (search.trim()) baseParams.set('search', search.trim());
      if (filterParams.isActive === 'true') baseParams.set('isActive', 'true');
      if (filterParams.isActive === 'false') baseParams.set('isActive', 'false');
      baseParams.set('sortBy', filterParams.sortBy || 'first_name');
      baseParams.set('sortOrder', filterParams.sortOrder || 'asc');

      let url = `/students?${baseParams.toString()}`;

      if (filterParams.learningLevelId) {
        const levelParams = new URLSearchParams();
        levelParams.set('page', page);
        levelParams.set('limit', itemsPerPage);
        if (search.trim()) levelParams.set('search', search.trim());
        if (filterParams.isActive === 'true') levelParams.set('isActive', 'true');
        if (filterParams.isActive === 'false') levelParams.set('isActive', 'false');
        url = `/students/level/${filterParams.learningLevelId}?${levelParams.toString()}`;
      } else if (filterParams.departmentId) {
        const departmentParams = new URLSearchParams();
        departmentParams.set('page', page);
        departmentParams.set('limit', itemsPerPage);
        departmentParams.set('departmentId', filterParams.departmentId);
        if (search.trim()) departmentParams.set('search', search.trim());
        if (filterParams.isActive === 'true') departmentParams.set('isActive', 'true');
        if (filterParams.isActive === 'false') departmentParams.set('isActive', 'false');
        url = `/students/organization/filter?${departmentParams.toString()}`;
      }

      const response = await api.get(url);
      if (response.data.success && response.data.data) {
        const data = response.data.data;

        if (filterParams.learningLevelId && data.level) {
          const levelStudents = (data.students || []).map((s) => ({
            ...s,
            level_name: s.level_name || data.level.name
          }));
          setStudents(levelStudents);
          setTotalPages(data.pagination?.pages || 1);
          setTotalStudents(data.pagination?.total || 0);
          setCurrentPage(data.pagination?.page || page);
        } else {
          setStudents(data.students || []);
          setTotalPages(data.pagination?.pages || 1);
          setTotalStudents(data.pagination?.total || 0);
          setCurrentPage(data.pagination?.page || page);
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(t('users.students.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const fetchDepartmentsForFilter = async () => {
    setDepartmentsLoading(true);
    try {
      let allDepartments = [];
      let depPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await api.get(`/organization/departments?page=${depPage}&limit=100`);
        if (response.data?.success) {
          const fetched = response.data.data?.departments || [];
          allDepartments = [...allDepartments, ...fetched];
          const pagination = response.data.data?.pagination;
          if (pagination && depPage < pagination.pages) {
            depPage += 1;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setFilterDepartments(allDepartments);
    } catch (error) {
      console.error('Error fetching departments for filter:', error);
      toast.error(t('users.students.departmentsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // Fetch learning levels
  const fetchLearningLevels = async () => {
    try {
      const response = await api.get('/organization/learning-levels?page=1&limit=100');
      if (response.data.success) {
        const levels = response.data.data.learningLevels.map(level => ({
          ...level,
          translatedName: learningLevelsData[level.name] ? 
            learningLevelsData[level.name][currentLanguage] || level.name : 
            level.name
        }));
        setLearningLevels(levels);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
    }
  };

  // Initial load and reset when competitionId changes — نفس تدفق الفلاتر كصفحة الطلبة
  useEffect(() => {
    setCompetition(null);
    setStudents([]);
    setSelectedStudents(new Set());
    setExistingParticipants(new Set());
    const reset = { ...DEFAULT_STUDENT_FILTERS };
    setFilters(reset);
    setAppliedFilters(reset);
    setSearchTerm('');
    setCurrentPage(1);

    fetchCompetitionDetails();
    fetchLearningLevels();
    fetchExistingParticipants();
    fetchDepartmentsForFilter();
    fetchStudents(1, '', reset);
  }, [competitionId]);

  // بحث مؤجل عند وجود نص فقط (التحميل الأول يتم من useEffect الخاص بـ competitionId)
  useEffect(() => {
    if (searchTerm.length === 0) return;
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchStudents(1, searchTerm, appliedFilters).finally(() => {
        setIsSearching(false);
      });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const isInitialLimitMount = useRef(true);
  useEffect(() => {
    if (isInitialLimitMount.current) {
      isInitialLimitMount.current = false;
      return;
    }
    setCurrentPage(1);
    fetchStudents(1, searchTerm, appliedFilters, limit);
  }, [limit]);

  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    fetchStudents(1, searchTerm, appliedFilters);
  };

  const applyFilters = () => {
    const newFilters = { ...filters };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchStudents(1, searchTerm, newFilters);
  };

  const clearFilters = () => {
    const cleared = { ...DEFAULT_STUDENT_FILTERS };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setSearchTerm('');
    setCurrentPage(1);
    fetchStudents(1, '', cleared);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (appliedFilters.isActive !== '') count++;
    if (appliedFilters.learningLevelId) count++;
    if (appliedFilters.departmentId) count++;
    return count;
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchStudents(page, searchTerm, appliedFilters);
    setSelectedStudents(new Set()); // Clear selection when changing page
  };

  // Toggle student selection (مفاتيح نصية موحّدة مع existingParticipants)
  const toggleStudentSelection = (userId) => {
    const key = String(userId);
    if (existingParticipants.has(key)) {
      return;
    }

    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Toggle all students on current page
  const toggleAllStudents = () => {
    const availableKeys = students
      .filter((s) => !existingParticipants.has(String(s.user_id)))
      .map((s) => String(s.user_id));
    const allSelected =
      availableKeys.length > 0 && availableKeys.every((k) => selectedStudents.has(k));
    if (allSelected) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(availableKeys));
    }
  };

  // Add selected students to competition
  const addStudentsToCompetition = async () => {
    if (selectedStudents.size === 0) {
      toast.error(t('competitions.selectStudentsFirst'), { duration: 3000, position: 'top-center' });
      return;
    }

    if (!competition) {
      toast.error(t('competitions.notFound'), { duration: 3000, position: 'top-center' });
      return;
    }

    setIsAdding(true);
    try {
      // إرسال user_ids (وليس student.ids) لأن الباك آند يتوقع user_ids
      const requestData = {
        studentIds: Array.from(selectedStudents, (id) => (Number.isFinite(Number(id)) ? Number(id) : id))
      };
      
      // استخدام competitionId (الكود الفريد) بدلاً من id الرقمي
      const response = await api.post(`/competitions/${competition.competitionId}/participants`, requestData);
      
      if (response.data.success) {
        // استخدام الترجمة المحلية مع عدد الطلاب المضافين
        const addedCount = selectedStudents.size;
        toast.success(t('competitions.studentsAddedSuccessCount', { count: addedCount }), {
          duration: 3000,
          position: 'top-center'
        });
        setSelectedStudents(new Set());
        // إعادة جلب المشاركين الحاليين بعد الإضافة
        fetchExistingParticipants();
        fetchStudents(currentPage, searchTerm, appliedFilters);
      }
    } catch (error) {
      console.error('Error adding students to competition:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message ||
                          t('competitions.studentsAddedError');
      
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('common.back')}
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrophyIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('competitions.manageStudents')}
              </h1>
              <p className="text-gray-600 mt-1">
                {competition?.name || t('common.loading')}
              </p>
            </div>
          </div>
          
          {selectedStudents.size > 0 && (
            <button
              onClick={addStudentsToCompetition}
              disabled={isAdding}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center disabled:opacity-50"
            >
              <UserPlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {isAdding 
                ? t('competitions.adding') 
                : t('competitions.addSelectedStudents', { count: selectedStudents.size })
              }
            </button>
          )}
        </div>
      </div>

      {/* Competition Scope Info */}
      {competition && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-1">
            {t('competitions.competitionScope')}:
          </p>
          <p className="text-sm text-gray-900">
            {(() => {
              const scope = competition.scope;
              if (scope?.departmentId) return `📁 ${t('competitions.departmentScope')}`;
              if (scope?.institutionId) return `🏢 ${t('competitions.institutionScope')}`;
              if (scope?.districtId) return `📍 ${t('competitions.districtScope')}`;
              if (scope?.academyId) return `🎓 ${t('competitions.academyScope')}`;
              if (scope?.regionId) return `🗺️ ${t('competitions.regionScope')}`;
              if (scope?.countryId) return `🌍 ${t('competitions.countryScope')}`;
              return t('common.notAvailable');
            })()}
          </p>
        </div>
      )}

      {/* Selected Count */}
      {selectedStudents.size > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            {t('competitions.selectedCount', { count: selectedStudents.size })}
          </p>
        </div>
      )}

      {/* نفس فلتر صفحة إدارة الطلبة (Students) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4 space-y-4">
          <div className={`flex flex-col md:flex-row gap-4 w-full ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input
                  type="text"
                  placeholder={t('competitions.searchStudentsPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearchTerm(v);
                    if (v === '') {
                      setCurrentPage(1);
                      fetchStudents(1, '', appliedFilters);
                    }
                  }}
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`}
                />
                {isSearching && (
                  <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
                  </div>
                )}
              </div>
            </div>
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.students.status')}</label>
              <select
                value={filters.isActive}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, isActive: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="">{t('users.students.allStatuses')}</option>
                <option value="true">{t('users.students.active')}</option>
                <option value="false">{t('users.students.inactive')}</option>
              </select>
            </div>
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.students.level')}</label>
              <select
                value={filters.learningLevelId}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, learningLevelId: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="">{t('users.students.allLevels')}</option>
                {learningLevels.map((level) => (
                  <option key={level.id} value={level.id}>{level.translatedName || level.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={`flex flex-col md:flex-row gap-4 w-full ${isRTL ? 'md:flex-row-reverse' : ''}`}>
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.students.department')}</label>
              <select
                value={filters.departmentId}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, departmentId: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                disabled={departmentsLoading}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('users.students.allDepartments')}</option>
                {filterDepartments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {getDepartmentTranslation(dept.name, currentLanguage)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:flex-1">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.students.sortBy')}</label>
              <select
                value={filters.sortBy}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, sortBy: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="first_name">{t('users.students.sortByFirstName')}</option>
                <option value="last_name">{t('users.students.sortByLastName')}</option>
                <option value="student_number">{t('users.students.sortByStudentNumber')}</option>
                <option value="enrollment_date">{t('users.students.sortByEnrollmentDate')}</option>
              </select>
            </div>
            <div className="md:flex-1 md:max-w-[9rem]">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.students.sortOrder')}</label>
              <select
                value={filters.sortOrder}
                onChange={(e) => {
                  const val = e.target.value;
                  const newFilters = { ...filters, sortOrder: val };
                  setFilters(newFilters);
                  setAppliedFilters(newFilters);
                  setCurrentPage(1);
                  fetchStudents(1, searchTerm, newFilters);
                }}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                <option value="asc">{t('users.students.sortAsc')}</option>
                <option value="desc">{t('users.students.sortDesc')}</option>
              </select>
            </div>
            <div className="md:flex-1 md:max-w-[9rem]">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors"
              >
                {LIMIT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2 md:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">
                {t('users.students.applyFilters')}
              </button>
              {(getActiveFiltersCount() > 0 || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors">
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-gray-600`}>{t('common.loading')}</span>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('competitions.noStudents')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('competitions.noStudentsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <input
                        type="checkbox"
                        checked={(() => {
                          const keys = students
                            .filter((s) => !existingParticipants.has(String(s.user_id)))
                            .map((s) => String(s.user_id));
                          return keys.length > 0 && keys.every((k) => selectedStudents.has(k));
                        })()}
                        onChange={toggleAllStudents}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('users.students.name')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      User ID
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('users.students.studentNumber')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('users.students.level')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student) => {
                    const uid = String(student.user_id);
                    const isAlreadyAdded = existingParticipants.has(uid);
                    const isSelected = selectedStudents.has(uid);

                    return (
                    <tr 
                      key={student.id} 
                      className={`
                        ${isAlreadyAdded ? 'bg-gray-100 opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
                        ${isSelected && !isAlreadyAdded ? 'bg-blue-50' : ''}
                      `}
                      onClick={() => !isAlreadyAdded && toggleStudentSelection(student.user_id)}
                    >
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isAlreadyAdded}
                          onChange={() => toggleStudentSelection(student.user_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <GraduationCapIcon className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className={`${isRTL ? 'mr-4' : 'ml-4'}`}>
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {student.first_name} {student.last_name}
                              </div>
                              {isAlreadyAdded && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <CheckCircleIcon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                  {t('competitions.alreadyAdded')}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{student.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          #{student.user_id}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {student.student_number}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm text-gray-900">
                          {translateLevelName(student.level_name)}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <div className={`bg-gray-50 px-6 py-4 border-t border-gray-200`}>
                <div className={`flex flex-col sm:flex-row items-center ${isRTL ? 'sm:flex-row-reverse' : ''} justify-between gap-4`}>
                  {/* Info */}
                  <div className="text-sm text-gray-700">
                    {t('competitions.showing')} <span className="font-bold text-primary-600">{((currentPage - 1) * limit) + 1}</span> {t('competitions.to')}{' '}
                    <span className="font-bold text-primary-600">{Math.min(currentPage * limit, totalStudents)}</span> {t('competitions.of')}{' '}
                    <span className="font-bold text-primary-600">{totalStudents}</span> {t('competitions.students')}
                  </div>
                  
                  {/* Page Numbers */}
                  <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse' : ''} items-center gap-2`}>
                    {/* Previous */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.previous')}
                    </button>
                    
                    {/* Page Numbers */}
                    <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse' : ''} items-center gap-1`}>
                      {/* First Page */}
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-2 text-gray-400">...</span>}
                        </>
                      )}
                      
                      {/* Pages around current */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        if (pageNum < 1 || pageNum > totalPages) return null;
                        if (currentPage > 3 && pageNum === 1) return null;
                        if (currentPage < totalPages - 2 && pageNum === totalPages) return null;
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {/* Last Page */}
                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-2 text-gray-400">...</span>}
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Next */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Action Button for Mobile */}
      {selectedStudents.size > 0 && (
        <div className="fixed bottom-6 right-6 md:hidden">
          <button
            onClick={addStudentsToCompetition}
            disabled={isAdding}
            className="bg-primary-600 text-white p-4 rounded-full shadow-lg hover:bg-primary-700 transition-colors flex items-center disabled:opacity-50"
          >
            <UserPlusIcon className="h-6 w-6" />
            <span className="ml-2 font-medium">{selectedStudents.size}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CompetitionStudents;

